import React, { useMemo, useRef, useState, useEffect } from "react";
import { useHistory, useParams, Link as RouterLink } from "react-router-dom";
import {
  Breadcrumbs,
  CircularProgress,
  Collapse,
  Link,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { CaretRight } from "@phosphor-icons/react";
import { format } from "date-fns";

import type { GoalMenuState } from "components/ClientDetails/Sections/GoalsAndTargets/shared/types";
import type { GoalResource } from "components/ClientDetails/Sections/GoalsAndTargets/shared/ResourcesCard";
import ResourcesCard from "./shared/ResourcesCard";
import GoalOverviewCard from "./Goals/GoalOverviewCard";
import GoalDetailTitleBar from "./Goals/GoalDetailTitleBar";
import TargetActionMenu from "./Targets/TargetActionMenu";
import ToggleChartGroup from "./shared/ToggleChartGroup";
import SessionHistory from "./Targets/SessionHistory";
import TargetChartArea from "./Targets/TargetChartArea";
import type { MasteryBannerPhase } from "./utils/evaluateMasteryBanner";
import { deriveMasteryBannerFromFlags } from "./utils/deriveMasteryBannerFromFlags";
import { toBannerCriteria } from "./utils/deriveStatusInsights";
import type { SessionEntry, AppointmentDetails } from "./Targets/SessionHistory";
import type { Goal, TargetSessionAppointmentDetails } from "api/types/aba";
import { normalizeDataPointsArray } from "../../../../utils/normalizeDataPointKeys";
import useGetDetailTarget from "hooks/api/goals/useGetDetailTarget";
import useGetListOfGoals from "hooks/api/goals/useGetListOfGoals";
import useArchiveTarget from "hooks/api/targets/useArchiveTarget";
import useUnarchiveTarget from "hooks/api/targets/useUnarchiveTarget";
import useDeleteTarget from "hooks/api/targets/useDeleteTarget";
import useMoveTarget from "hooks/api/targets/useMoveTarget";
import useGetTargetSessionMetrics from "hooks/api/targets/useGetTargetSessionMetrics";
import useGetDetailGoal from "hooks/api/goals/useGetDetailGoal";
import useListTargetSessions from "hooks/api/targets/useListTargetSessions";
import useListTargetMarkers from "hooks/api/targets/useListTargetMarkers";
import useCreateTargetMarker from "hooks/api/targets/useCreateTargetMarker";
import useUpdateTargetMarker from "hooks/api/targets/useUpdateTargetMarker";
import useDeleteTargetMarker from "hooks/api/targets/useDeleteTargetMarker";
import type { TargetMarker } from "api/types/markers";

import MoveTargetPanel from "./Targets/MoveTargetPanel";
import DeleteTargetDialog from "./Targets/DeleteTargetDialog";
import MarkerDialog, {
  type MarkerSessionOption,
  type MarkerFormValues,
  type MarkerTargetsContext,
} from "./Targets/MarkerDialog";
import DeleteMarkerDialog, { type DeleteMarkerScope } from "./Targets/DeleteMarkerDialog";
import RecordNewSessionDialog from "./Targets/RecordNewSessionDialog";
import { selectMoveDestinationGoals } from "./utils/goalSelectors";
import type { GoalType } from "./Goals/GoalOverviewCard";
import MasteryBanner from "./MasteryBanner";
import PreviewCard from "./Targets/TargetBuilderPanel/preview/PreviewCard";
import {
  TargetCriteriaRows,
  TargetMeasurementRows,
  labelFor,
} from "./Targets/TargetBuilderPanel/preview/TargetValueRows";
import {
  IMPLEMENTATION_SETTING_OPTIONS,
  PRIMARY_IMPLEMENTER_OPTIONS,
  targetToFormValues,
} from "./Targets/TargetBuilderPanel/TargetBuilderPanel.types";
import { getAnalyticsToggleOptions } from "./Targets/TargetBuilderPanel/l-model/analyticsToggleOptions";
import {
  resolveMarkerTargetIds,
  toCreateMarkerBody,
  toMarkerFormValues,
  toUpdateMarkerBody,
} from "./utils/markerMappers";
import { hasDataForChartOption } from "./utils/hasDataForChartOption";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toGoalType(targetType: string): GoalType {
  return targetType === "behavior-reduction" ? "behaviorReduction" : "skillAcquisition";
}

/** Formats "10:03 am - 11:18 am (PST)" from ISO datetimes + IANA timezone. */
function formatAppointmentTimeRange(
  startDate: string | null,
  endDate: string | null,
  timezone: string | null
): string | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  // An unrecognized IANA zone from the API throws a RangeError as soon as
  // Intl.DateTimeFormat is constructed with it — fall back to formatting
  // without a timezone instead of crashing the Target Detail page.
  let safeTimezone = timezone;
  if (safeTimezone) {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: safeTimezone });
    } catch {
      safeTimezone = null;
    }
  }

  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(safeTimezone ? { timeZone: safeTimezone } : {}),
  });
  const startLabel = timeFormatter.format(start).toLowerCase();
  const endLabel = timeFormatter.format(end).toLowerCase();

  const tzLabel = safeTimezone
    ? new Intl.DateTimeFormat("en-US", { timeZone: safeTimezone, timeZoneName: "short" })
        .formatToParts(start)
        .find((part) => part.type === "timeZoneName")?.value
    : undefined;

  return `${startLabel} - ${endLabel}${tzLabel ? ` (${tzLabel})` : ""}`;
}

/**
 * Formats just the calendar date portion of an ISO datetime, anchored to
 * noon UTC so the browser's local timezone can never shift it by a day —
 * the scheduled date is treated as authoritative as written, the same
 * defensive pattern svc-centralReach's session-start `date` handling uses.
 */
function formatScheduledDateLabel(scheduledDate: string | null): string {
  const match = scheduledDate ? /^(\d{4})-(\d{2})-(\d{2})/.exec(scheduledDate) : null;
  if (!match) return "Unknown date";
  const utcNoon = new Date(`${match[0]}T12:00:00Z`);
  if (Number.isNaN(utcNoon.getTime())) return "Unknown date";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(utcNoon);
}

/**
 * Formats the actual call duration (in whole minutes) from `start_date`/
 * `end_date`, falling back to the scheduled `duration_minutes` when the
 * actual timestamps are missing or invalid — e.g. a call that was scheduled
 * for 60 minutes but only lasted 1 should show "1 minutes", not "60 minutes".
 *
 * Minutes are floored independently (not rounded from the raw elapsed
 * seconds) so this always agrees with `formatAppointmentTimeRange`, which
 * likewise truncates seconds — otherwise a call spanning e.g. 12:27:50 to
 * 12:28:40 would show "2 minutes" next to a "12:27 pm - 12:28 pm" range
 * that only spans 1 minute.
 */
function formatAppointmentDuration(
  startDate: string | null,
  endDate: string | null,
  scheduledDurationMinutes: number | null
): string {
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end >= start) {
      const startMinute = Math.floor(start.getTime() / 60000);
      const endMinute = Math.floor(end.getTime() / 60000);
      return `${endMinute - startMinute} minutes`;
    }
  }
  return scheduledDurationMinutes != null ? `${scheduledDurationMinutes} minutes` : "Unknown duration";
}

/**
 * Shapes svc-centralReach's best-effort an-api enrichment into the display
 * strings SessionHistory's "Appointment Details" card expects.
 */
function formatAppointmentDetails(details: TargetSessionAppointmentDetails): AppointmentDetails {
  return {
    dateLabel: formatScheduledDateLabel(details.scheduled_date),
    durationLabel: formatAppointmentDuration(
      details.start_date,
      details.end_date,
      details.duration_minutes
    ),
    timeRangeLabel:
      formatAppointmentTimeRange(details.start_date, details.end_date, details.timezone) ??
      undefined,
    diagnosisLabel: details.diagnosis ?? undefined,
    deliveryMethod: details.is_video_call ? "Telehealth" : "In-Person",
    billableTimeId: details.billable_time_id ?? undefined,
  };
}

function mapResources(
  resources: Array<{ url: string; title: string }> | null | undefined
): GoalResource[] {
  if (!resources) return [];
  return resources.map((r, i) => ({ id: String(i), label: r.title, url: r.url }));
}

/**
 * Resolves whether count_prompted_as_correct is enabled for a target.
 * Supports both transport (root-level flag) and legacy FE-stored (inside
 * prompt_hierarchy object) shapes.
 */
function resolveCountPromptedAsCorrect(step35Config: unknown): boolean {
  if (!step35Config || typeof step35Config !== "object") return false;
  const cfg = step35Config as Record<string, unknown>;
  if (typeof cfg.count_prompted_as_correct === "boolean") return cfg.count_prompted_as_correct;
  const ph = cfg.prompt_hierarchy;
  if (ph && typeof ph === "object" && !Array.isArray(ph)) {
    const flag = (ph as Record<string, unknown>).count_prompted_as_correct;
    if (typeof flag === "boolean") return flag;
  }
  return false;
}

function resolveHasPromptHierarchy(step35Config: unknown): boolean {
  if (!step35Config || typeof step35Config !== "object") return false;
  const cfg = step35Config as Record<string, unknown>;
  const ph = cfg.prompt_hierarchy;
  if (Array.isArray(ph)) return ph.length > 0;
  if (ph && typeof ph === "object" && !Array.isArray(ph)) {
    const levels = (ph as Record<string, unknown>).levels;
    return Array.isArray(levels) && levels.length > 0;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Target detail view — rendered at /clients/:id/goals-targets/:goalId/targets/:targetId.
 * Displays the full detail of a single target including session data,
 * mastery criteria, and clinical metadata. Breadcrumb navigates through
 * Goals → Goal Name → Target Name.
 */
const TargetDetail: React.FC = () => {
  const history = useHistory();
  const {
    id: clientId,
    slug: goalId,
    subSlug: targetId,
  } = useParams<{
    id: string;
    slug: string;
    subSlug: string;
  }>();

  const { target, isTargetLoading } = useGetDetailTarget(Number(targetId));
  const { targetSessions, isLoadingTargetSessions } = useListTargetSessions(Number(targetId));
  const { markers } = useListTargetMarkers(Number(targetId));

  const [targetMenu, setTargetMenu] = useState<GoalMenuState>({
    anchorEl: null,
    goalId: null,
  });
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addMarkerOpen, setAddMarkerOpen] = useState(false);
  const [recordNewSessionOpen, setRecordNewSessionOpen] = useState(false);
  // "Create more" — read from a ref so the success handler sees the value as it
  // was when the marker was submitted, not a later toggle.
  const createMoreMarkersRef = useRef(false);
  const [markerResetToken, setMarkerResetToken] = useState(0);
  const { createMarkerAction, isCreatingMarker } = useCreateTargetMarker({
    onSuccess: () => {
      // With "create more" checked the dialog stays open and only clears the
      // entry fields, so the clinician can log the next marker right away.
      if (createMoreMarkersRef.current) setMarkerResetToken((token) => token + 1);
      else setAddMarkerOpen(false);
    },
  });

  // Edit / delete a marker picked from its chart menu. The marker is kept apart
  // from `open` so a closing dialog still has its label and values while it
  // animates out.
  const [markerToEdit, setMarkerToEdit] = useState<TargetMarker | null>(null);
  const [editMarkerOpen, setEditMarkerOpen] = useState(false);
  const [markerToDelete, setMarkerToDelete] = useState<TargetMarker | null>(null);
  const [deleteMarkerOpen, setDeleteMarkerOpen] = useState(false);
  const { updateMarkerAction, isUpdatingMarker } = useUpdateTargetMarker({
    onSuccess: () => setEditMarkerOpen(false),
  });
  const { deleteMarkerAction, isDeletingMarker } = useDeleteTargetMarker({
    onSuccess: () => setDeleteMarkerOpen(false),
  });
  // dismiss for the mastery banner
  const [isMasteryBannerDismissed, setIsMasteryBannerDismissed] = useState(false);
  // Collapsible right sidebar — collapsed lets the chart area span full width.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Session row highlighted in sync with the chart point under the pointer.
  const [highlightedSessionId, setHighlightedSessionId] = useState<string | null>(null);

  // Chart toggle tabs are derived from the target's L4 metric catalogue.
  // For counter_prompt_hierarchy, % Correct is only shown when count_prompted_as_correct is enabled.
  const countPromptedAsCorrect = useMemo(
    () => resolveCountPromptedAsCorrect(target?.step35_config),
    [target?.step35_config]
  );
  const hasPromptHierarchy = useMemo(
    () => resolveHasPromptHierarchy(target?.step35_config),
    [target?.step35_config]
  );
  const {
    sessionMetrics,
    stepAcquisitionSequence,
    primitiveType,
    behaviorFlags,
    trialsToCriterion,
    trend,
    visualAnalysis,
    isLoadingSessionMetrics,
    sessionMetricsError,
    refetch: refetchSessionMetrics,
  } = useGetTargetSessionMetrics(Number(targetId));

  // Only offer metrics this target has recorded data for — a candidate whose
  // backing field is null across all sessions (or, for insight cards, whose
  // derivation has no criterion/threshold to work from) would render an empty
  // state instead of a chart (AC3: no empty tabs offered).
  const chartOptions = useMemo(() => {
    const candidateOptions = getAnalyticsToggleOptions(target?.l1_dimension, target?.l3_context, {
      primitiveType,
      hasPromptHierarchy,
      countPromptedAsCorrect,
      behaviorSubject: target?.goal?.behavior_subject,
      targetType: target?.target_type,
    });
    return candidateOptions.filter((option) =>
      hasDataForChartOption(option, {
        sessionMetrics,
        stepAcquisitionSequence,
        targetType: target?.target_type,
        l1Dimension: target?.l1_dimension,
        phase: target?.phase as MasteryBannerPhase,
        masteryCriteria: target?.mastery_criteria,
        trend,
        visualAnalysis,
        trialsToCriterion,
      })
    );
  }, [
    target?.l1_dimension,
    target?.l3_context,
    primitiveType,
    hasPromptHierarchy,
    countPromptedAsCorrect,
    target?.goal?.behavior_subject,
    sessionMetrics,
    stepAcquisitionSequence,
    target?.target_type,
    target?.phase,
    target?.mastery_criteria,
    trend,
    visualAnalysis,
    trialsToCriterion,
  ]);
  const [selectedChart, setSelectedChart] = useState<string | null>(null);

  // Reset the selection when the available options change
  useEffect(() => {
    if (!selectedChart) return;
    const stillValid = chartOptions.some((option) => option.value === selectedChart);
    if (!stillValid) setSelectedChart(null);
  }, [chartOptions, selectedChart]);

  // Default to the first metric until the clinician picks one.
  const activeChart = selectedChart ?? chartOptions[0]?.value ?? null;

  // The full active toggle option — carries both the display label (used to
  // resolve line charts) and the canonical segment id (used to route cards).
  const activeOption = useMemo(
    () => chartOptions.find((option) => option.value === activeChart),
    [chartOptions, activeChart]
  );

  // Server-derived mastery/regression signal for the detail banner. Criteria +
  // phase supply the copy's numbers and transition suffix; the transition target
  // is read from the (post-transition) phase rather than assumed.
  const masteryBanner = useMemo(
    () =>
      deriveMasteryBannerFromFlags({
        flags: behaviorFlags,
        masteryCriteria: toBannerCriteria(target?.mastery_criteria, target?.l1_dimension),
        regressionCriteria: toBannerCriteria(target?.regression_criteria, target?.l1_dimension),
        phase: target?.phase as MasteryBannerPhase | undefined,
        targetType: target?.target_type,
        l1Dimension: target?.l1_dimension,
      }),
    [
      behaviorFlags,
      target?.mastery_criteria,
      target?.regression_criteria,
      target?.phase,
      target?.target_type,
      target?.l1_dimension,
    ]
  );

  // Session options for the Add Marker dialog, newest first so the most recent
  // session sits at the top of the dropdown.
  const markerSessionOptions = useMemo<MarkerSessionOption[]>(
    () =>
      sessionMetrics
        ?.map((m) => ({
          id: String(m.target_session_id),
          label: m.session_date
            ? `Session ${m.session_number} — ${new Date(m.session_date).toLocaleDateString()}`
            : `Session ${m.session_number}`,
        }))
        .reverse(),
    [sessionMetrics]
  );

  // Persist the captured marker; on mutation success the dialog either closes or
  // clears its entry fields, depending on "create more" (wired through the
  // hook's onSuccess above). The mapper bridges the form's FE shape to the API
  // contract.
  const handleAddMarker = (values: MarkerFormValues) => {
    createMoreMarkersRef.current = values.createMore;
    const targetIds = resolveMarkerTargetIds(values, markerTargetsContext);
    createMarkerAction({
      targetId: Number(targetId),
      data: toCreateMarkerBody(values, targetIds),
    });
  };

  // The chart reports marker ids; resolve them against the loaded markers and
  // ignore any that are already gone (e.g. deleted in the meantime).
  const handleEditMarker = (markerId: number) => {
    const marker = markers.find((candidate) => candidate.id === markerId);
    if (!marker) return;
    setMarkerToEdit(marker);
    setEditMarkerOpen(true);
  };

  const handleDeleteMarker = (markerId: number) => {
    const marker = markers.find((candidate) => candidate.id === markerId);
    if (!marker) return;
    setMarkerToDelete(marker);
    setDeleteMarkerOpen(true);
  };

  // An edit replaces the marker's whole target list, so the targets it applied
  // to before are passed along: their charts are refreshed too, and a target
  // dropped from the marker stops showing it.
  //
  // The list is only sent when the form could actually express it: with the
  // goals still loading the dialog hides the targets section, and resolving
  // them would come back as "this target only" and silently drop the rest.
  // Omitted, the backend keeps the stored list. A session-linked marker is the
  // exception — it applies to its own target, loaded goals or not.
  const handleUpdateMarker = (values: MarkerFormValues) => {
    if (!markerToEdit) return;
    const knowsTargets = markerTargetsContext.goalOptions.length > 0;
    const targetIds =
      knowsTargets || values.linkMode === "session"
        ? resolveMarkerTargetIds(values, markerTargetsContext)
        : undefined;
    updateMarkerAction({
      targetId: Number(targetId),
      markerId: markerToEdit.id,
      data: toUpdateMarkerBody(values, targetIds),
      affectedTargetIds: markerToEdit.target_ids,
    });
  };

  // A marker shared with other targets can be taken off this one alone or
  // deleted everywhere; the dialog asks and hands over the scope. Either way the
  // other targets' charts are refreshed, since the marker changed for them too.
  const handleConfirmDeleteMarker = (scope: DeleteMarkerScope) => {
    if (!markerToDelete) return;
    deleteMarkerAction({
      targetId: Number(targetId),
      markerId: markerToDelete.id,
      scope,
      affectedTargetIds: markerToDelete.target_ids,
    });
  };

  // Ad hoc sessions have no appointmentId. The bare `/data-sheets/{clientId}`
  // route is the "current targets" live view, not scoped to any one session —
  // wrong for backdated ad hoc entry, where the clinician needs the same
  // per-session control as a real visit. CR web's AdHocSessionResolver
  // mirrors AppointmentSessionResolver: it starts the session server-side
  // and redirects to `/session/{id}`, so land there instead.
  //
  // target_id/return_url are both optional and let the resolver pre-add the
  // target we launched from and send the clinician back to this exact page
  // when they hit Close in CR — otherwise the new session starts empty and
  // Close falls back to CR's generic goals-targets list.
  const handleRecordNewSession = (date: Date) => {
    setRecordNewSessionOpen(false);
    if (!process.env.CENTRAL_REACH_URL) return;
    const base = process.env.CENTRAL_REACH_URL.replace(/\/$/, "");
    const isoDate = format(date, "yyyy-MM-dd");
    const returnUrl = `${window.location.origin}/clients/${clientId}/goals-targets/${goalId}/target/${targetId}`;
    const params = new URLSearchParams({
      date: isoDate,
      target_id: targetId,
      return_url: returnUrl,
    });
    window.open(
      `${base}/data-sheets/${encodeURIComponent(clientId)}/new-session?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /** Navigate back to the parent goal after any destructive / move action. */
  const goBackToGoal = () => {
    history.push(`/clients/${clientId}/goals-targets/${goalId}`);
  };

  const { goals: allGoals } = useGetListOfGoals(clientId, { includeArchived: false });

  // Targets a new marker can also apply to. The list only holds non-archived
  // goals and targets — any non-archived target can have markers, and the
  // markers API rejects archived ones. `includeArchived: false` filters the
  // goals; their nested targets have to be filtered here.
  const markerTargetsContext = useMemo<MarkerTargetsContext>(
    () => ({
      goalOptions: allGoals.map((goal: Goal) => ({
        id: goal.id,
        title: goal.title,
        targets: (goal.targets ?? [])
          .filter((goalTarget) => !goalTarget.is_archived)
          .map((goalTarget) => ({
            id: goalTarget.id,
            title: goalTarget.title,
          })),
      })),
      currentGoalId: Number(goalId),
      currentTargetId: Number(targetId),
    }),
    [allGoals, goalId, targetId]
  );
  // Values the edit dialog opens with, including the targets the marker already
  // applies to — resolved here, once the goals are loaded.
  const markerToEditValues = useMemo(
    () => (markerToEdit ? toMarkerFormValues(markerToEdit, markerTargetsContext) : null),
    [markerToEdit, markerTargetsContext]
  );
  const { goal: fetchedGoal } = useGetDetailGoal(Number(goalId));

  const { archiveTargetAction } = useArchiveTarget({ onSuccess: goBackToGoal });
  // Unarchive keeps the user on the detail view — the invalidated target query
  // refreshes the archived state in place.
  const { unarchiveTargetAction } = useUnarchiveTarget();
  const { moveTargetAction, isMovingTarget } = useMoveTarget({
    onSuccess: () => {
      setMoveOpen(false);
      goBackToGoal();
    },
  });
  const { deleteTargetAction, isDeletingTarget } = useDeleteTarget({
    onSuccess: () => {
      setDeleteOpen(false);
      goBackToGoal();
    },
  });

  // Derived session lists and labels — compute unconditionally so hooks
  // order remains stable across renders (avoid conditional hooks after
  // early returns). These depend on the above query results but are
  // safe to compute even while loading.
  const sessions: SessionEntry[] = useMemo(() => {
    if (!sessionMetrics) return [];
    const targetSessionsById = new Map(targetSessions.map((s) => [s.id, s]));

    // sessionMetrics is oldest-first (the chart needs that for its left-to-
    // right timeline) — SessionHistory's list reads top-to-bottom, so reverse
    // to show the newest session first, matching markerSessionOptions above.
    return [...sessionMetrics].reverse().map((metric) => {
      const date = metric.session_date ? new Date(metric.session_date) : null;
      const validDate = date && !Number.isNaN(date.getTime()) ? date : null;
      const targetSession = targetSessionsById.get(metric.target_session_id);
      return {
        id: String(metric.target_session_id),
        clinicalSessionId:
          targetSession?.clinical_session_id != null
            ? String(targetSession.clinical_session_id)
            : undefined,
        dateLabel: validDate
          ? new Intl.DateTimeFormat("en-US", {
              month: "long",
              day: "numeric",
            }).format(validDate)
          : "Unknown date",
        sessionLabel: `Session ${metric.session_number}`,
        primitiveType: primitiveType,
        dataPoints: normalizeDataPointsArray(targetSession?.data?.map((d) => d.data_point) ?? []),
        appointmentId: targetSession?.clinical_session?.appointment_id ?? undefined,
        // Ad hoc sessions (no linked appointment) leave this undefined —
        // SessionHistory only renders "Appointment Details" when present.
        appointmentDetails: targetSession?.clinical_session?.appointment_details
          ? formatAppointmentDetails(targetSession.clinical_session.appointment_details)
          : undefined,
        metrics: {
          percentCorrect: metric.percent_correct,
          percentIndependent: metric.percent_independent,
          promptLevelDistribution: metric.prompt_level_distribution,
          countPerSession: metric.count_per_session,
          percentStepsCorrect: metric.percent_steps_correct,
          percentStepsIndependent: metric.percent_steps_independent,
          chainCompletionRate: metric.chain_completion_rate,
          boutCount: metric.bout_count,
          totalDurationMs: metric.total_duration_ms,
          meanDurationMs: metric.mean_duration_ms,
          meanLatencyMs: metric.mean_latency_ms,
          latencyByPromptLevel: metric.latency_by_prompt_level,
          meanRating: metric.mean_rating,
        },
      };
    });
  }, [sessionMetrics, targetSessions, primitiveType, countPromptedAsCorrect]);

  const sessionCount = sessions.length;

  const lastSessionLabel = useMemo(() => {
    const lastSessionDate = sessionMetrics?.[sessionMetrics.length - 1]?.session_date;
    if (!lastSessionDate) return "No sessions";

    const parsedDate = new Date(lastSessionDate);
    if (Number.isNaN(parsedDate.getTime())) return "Unknown";

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(parsedDate);
  }, [sessionMetrics]);

  const handleMenuOpen = (id: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    setTargetMenu({ anchorEl: e.currentTarget, goalId: id });
  };

  const handleMenuClose = () => setTargetMenu({ anchorEl: null, goalId: null });

  const handleEditTarget = () => {
    handleMenuClose();
    history.push(`/clients/${clientId}/goals-targets/${goalId}/target/${targetId}?mode=edit`);
  };

  const handleArchiveTarget = () => {
    archiveTargetAction({ targetId: Number(targetId), goalId: Number(goalId) });
  };

  const handleUnarchiveTarget = () => {
    unarchiveTargetAction({ targetId: Number(targetId), goalId: Number(goalId) });
  };

  const handleConfirmMove = (destinationGoalId: string) => {
    moveTargetAction({
      targetId: Number(targetId),
      sourceGoalId: Number(goalId),
      destinationGoalId: Number(destinationGoalId),
    });
  };

  const handleConfirmDelete = () => {
    deleteTargetAction({ targetId: Number(targetId), goalId: Number(goalId) });
  };

  if (isTargetLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress aria-label="Loading target" />
      </Box>
    );
  }

  if (!target) return null;

  const goal = target.goal;

  // Display rule: archived targets show "discontinued" as their status —
  // purely visual, the API status is untouched.
  const displayStatus = target.is_archived ? "discontinued" : target.status;

  /**
   * Move destinations: every active goal for this client that is not the
   * current goal and matches behavior_subject + goal_type. Shared business
   * rule lives in `selectMoveDestinationGoals` (also used by GoalDetails).
   *
   * Per the API contract, target.goal is the full Goal schema (including
   * behavior_subject and goal_type). Use fetchedGoal when available for
   * freshness; fall back to the embedded goal which is always present.
   */
  const sourceGoal = fetchedGoal ?? goal;
  const moveDestinationGoals = selectMoveDestinationGoals(allGoals, sourceGoal);

  // Normalized form-values shape — same source the builder preview renders
  // from, so the sidebar mirrors every field the builder captures.
  const formValues = targetToFormValues(target);

  return (
    <Stack direction="column" gap={5} p={6}>
      {/* Breadcrumb: Goals > Goal Name > Target Name */}
      <Breadcrumbs separator={<CaretRight />} aria-label="breadcrumb">
        <Link
          component={RouterLink}
          to={`/clients/${clientId}/goals-targets`}
          variant="body2"
          underline="hover"
        >
          Goals
        </Link>

        <Link
          component={RouterLink}
          to={`/clients/${clientId}/goals-targets/${goalId}`}
          variant="body2"
          underline="hover"
        >
          {goal.title}
        </Link>

        <Typography component="span" variant="body2" aria-current="page">
          {target.title}
        </Typography>
      </Breadcrumbs>

      {/* Title bar */}
      <GoalDetailTitleBar
        goalName={target.title}
        description={`Goal: ${goal.title}`}
        clinicalPhase={target.phase}
        status={displayStatus}
        onAddToProgram={() => {}}
        onMenuOpen={handleMenuOpen(target.id)}
        onToggleSidebar={() => setIsSidebarCollapsed((prev) => !prev)}
        isSidebarCollapsed={isSidebarCollapsed}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
        }}
      >
        {/* Main content area — grows to fill the space the sidebar leaves
            behind, animating in sync with the collapse transition. */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {!isMasteryBannerDismissed && masteryBanner && (
            <MasteryBanner
              level={masteryBanner.level}
              title={masteryBanner.title}
              detail={masteryBanner.detail}
              onDismiss={() => setIsMasteryBannerDismissed(true)}
            />
          )}

          {chartOptions.length > 0 && activeChart && (
            <Box mt={5}>
              <ToggleChartGroup
                value={activeChart}
                onChange={setSelectedChart}
                aria-label="Select metric to display"
                options={chartOptions}
              />
            </Box>
          )}

          {/* Chart area — a single dispatcher renders the right visualization
              (insight/summary card, line chart, or loading/error/empty state)
              for the active metric segment. */}
          <Box mt={5}>
            <TargetChartArea
              activeOption={activeOption}
              sessionMetrics={sessionMetrics}
              stepAcquisitionSequence={stepAcquisitionSequence}
              markers={markers}
              isLoading={isLoadingSessionMetrics}
              error={sessionMetricsError}
              onRetry={() => refetchSessionMetrics()}
              targetType={target.target_type}
              l1Dimension={target.l1_dimension}
              phase={target.phase as MasteryBannerPhase}
              masteryCriteria={target.mastery_criteria}
              trend={trend}
              visualAnalysis={visualAnalysis}
              trialsToCriterion={trialsToCriterion}
              onAddMarker={() => setAddMarkerOpen(true)}
              onEditMarker={handleEditMarker}
              onDeleteMarker={handleDeleteMarker}
              onHoverSessionChange={setHighlightedSessionId}
            />
          </Box>

          {/* Session history */}
          <Box mt={5}>
            {isLoadingSessionMetrics || isLoadingTargetSessions ? (
              <CircularProgress aria-label="Loading session history" size={24} />
            ) : (
              <SessionHistory
                sessionCount={sessionCount}
                lastSessionLabel={lastSessionLabel}
                sessions={sessions}
                onOpenDataSheet={
                  process.env.CENTRAL_REACH_URL
                    ? (sessionId) => {
                        if (!sessionId) return;
                        const base = process.env.CENTRAL_REACH_URL!.replace(/\/$/, "");
                        window.open(
                          `${base}/data-sheets/${encodeURIComponent(clientId)}/session/${encodeURIComponent(sessionId)}`,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    : undefined
                }
                onOpenAppointmentDetails={(session) => {
                  // Opens Billing's session-note review dialog for this
                  // appointment — an answersnow-web route (not CR web), via
                  // the same ?billableTimeId= deep link the billing table's
                  // own row menu uses (BillingItems/index.js's useHandleUrlParam).
                  const billableTimeId = session.appointmentDetails?.billableTimeId;
                  if (billableTimeId == null) return;
                  window.open(
                    `/billing?billableTimeId=${encodeURIComponent(String(billableTimeId))}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
                onRecordNewSession={
                  process.env.CENTRAL_REACH_URL ? () => setRecordNewSessionOpen(true) : undefined
                }
                highlightedSessionId={highlightedSessionId}
              />
            )}
          </Box>
        </Box>

        {/* Sidebar — mirrors the builder preview: overview, measurement &
            instruction design, and the five criteria, all from the same
            normalized form-values shape. Collapses horizontally instead of
            disappearing abruptly. */}
        <Collapse
          in={!isSidebarCollapsed}
          orientation="horizontal"
          sx={{ alignSelf: "stretch", flexShrink: 0 }}
        >
          <Box
            data-testid="target-sidebar"
            aria-hidden={isSidebarCollapsed}
            sx={{ width: { xs: "100%", md: 360 }, pl: { md: 5 }, pt: { xs: 5, md: 0 } }}
          >
            <GoalOverviewCard
              title="Target Overview"
              clinicalPhase={target.phase}
              status={displayStatus}
              goalType={toGoalType(target.target_type)}
              behaviorMeasured={goal.behavior_subject}
              replacementBehavior={
                target.target_type === "skill-acquisition"
                  ? Boolean(target.is_replacement_behavior)
                  : undefined
              }
              implementerLabel={labelFor(
                formValues.primary_implementer,
                PRIMARY_IMPLEMENTER_OPTIONS
              )}
              settingLabel={labelFor(
                formValues.implementation_setting,
                IMPLEMENTATION_SETTING_OPTIONS
              )}
              lastSessionDate={null}
            />

            <Box mt={5}>
              <PreviewCard title="Measurement & instruction design">
                <TargetMeasurementRows
                  values={formValues}
                  behaviorSubject={target?.goal?.behavior_subject}
                />
              </PreviewCard>
            </Box>

            <Box mt={5}>
              <PreviewCard title="Mastery criteria">
                <TargetCriteriaRows values={formValues} />
              </PreviewCard>
            </Box>

            {target.resources && target.resources.length > 0 && (
              <Box mt={5}>
                <ResourcesCard resources={mapResources(target.resources)} />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      <TargetActionMenu
        anchorEl={targetMenu.anchorEl}
        open={Boolean(targetMenu.anchorEl)}
        onClose={handleMenuClose}
        onEdit={handleEditTarget}
        onMove={() => setMoveOpen(true)}
        onArchive={handleArchiveTarget}
        onUnarchive={handleUnarchiveTarget}
        isArchived={Boolean(target.is_archived)}
        onDelete={() => setDeleteOpen(true)}
      />

      <MoveTargetPanel
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        targetName={target.title}
        availableGoals={moveDestinationGoals}
        onMove={handleConfirmMove}
        isLoading={isMovingTarget}
      />

      <DeleteTargetDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        targetName={target.title}
        onConfirm={handleConfirmDelete}
        isLoading={isDeletingTarget}
      />

      <MarkerDialog
        open={addMarkerOpen}
        onClose={() => setAddMarkerOpen(false)}
        sessions={markerSessionOptions}
        onSubmit={handleAddMarker}
        isLoading={isCreatingMarker}
        resetToken={markerResetToken}
        // Withheld while the goals load, so the section never opens on an empty picker.
        targetsContext={
          markerTargetsContext.goalOptions.length > 0 ? markerTargetsContext : undefined
        }
      />

      {/* Separate instance from "add", so closing an edit never flips the
          dialog's title/actions mid-transition. */}
      {markerToEditValues && (
        <MarkerDialog
          mode="edit"
          initialValues={markerToEditValues}
          open={editMarkerOpen}
          onClose={() => setEditMarkerOpen(false)}
          sessions={markerSessionOptions}
          onSubmit={handleUpdateMarker}
          isLoading={isUpdatingMarker}
          // Withheld while the goals load, so the section never opens on an empty picker.
          targetsContext={
            markerTargetsContext.goalOptions.length > 0 ? markerTargetsContext : undefined
          }
        />
      )}

      <DeleteMarkerDialog
        open={deleteMarkerOpen}
        markerLabel={markerToDelete?.label ?? ""}
        // More than one: the dialog asks where to remove the marker from.
        appliedTargetCount={markerToDelete?.target_ids?.length ?? 1}
        onClose={() => setDeleteMarkerOpen(false)}
        onConfirm={handleConfirmDeleteMarker}
        isLoading={isDeletingMarker}
      />

      <RecordNewSessionDialog
        open={recordNewSessionOpen}
        onClose={() => setRecordNewSessionOpen(false)}
        onSubmit={handleRecordNewSession}
      />
    </Stack>
  );
};

export default TargetDetail;
